import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {Box, Grid} from '@mui/material';
import {useForm, FormProvider} from 'react-hook-form';
import {toast} from 'react-toastify';
import ModalWrapper, {ModalContentWrapper, ModalLoader} from '../../../pieces/modals/modalWrapper';
import CustomFormBlock from '../../../pieces/inputs/formBlock';
import {inputTypes} from '../../../pieces/inputs/formInput';
import FormSubmitButtons from '../../../pieces/formSubmitButtons';
import {Text} from '../../../pieces/text';
import Spacer from '../../../pieces/spacer';
import ApiUtils from '../../../utils/apiUtils';
import * as STYLE_CONSTANTS from '../../../styles/styleConstants';

const TARGET_COMPANY_FIELD = {
  api_name: 'targetCompanyId',
  required: true,
  label: 'Transfer to',
  display_label: 'Transfer to',
};

const TOAST_ID = 'transfer_jobs_company';

/**
 * Moves the selected draft jobs to another company.
 *
 * The destination list comes from the server rather than the full company list,
 * because a transfer is only allowed within the source company's own tree -- a
 * parent, a sub, or a sibling. Offering every company would mostly offer choices
 * the transfer would then reject.
 */
const TransferJobsModal = (props) => {
  const {open, jobs, sourceCompany, handleClose, onTransferred} = props;

  const [options, setOptions] = useState(null);
  const [optionsError, setOptionsError] = useState(null);
  const [withheldCount, setWithheldCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm({
    defaultValues: {[TARGET_COMPANY_FIELD.api_name]: ''},
    mode: 'onChange',
  });

  const targetCompanyId = form.watch(TARGET_COMPANY_FIELD.api_name);
  const sourceCompanyId = sourceCompany?.id;

  useEffect(() => {
    if (!open || !sourceCompanyId) return;

    let cancelled = false;

    const fetchOptions = async () => {
      setOptions(null);
      setOptionsError(null);
      setWithheldCount(0);
      form.setValue(TARGET_COMPANY_FIELD.api_name, '');

      const response = await ApiUtils.makeApiCall(
        'GET',
        `jobs/transfer-company-options?companyId=${encodeURIComponent(sourceCompanyId)}`
      );
      if (cancelled) return;

      if (response?.status !== 'success') {
        setOptionsError(response?.message || 'Could not load the list of companies.');
        setOptions([]);
        return;
      }
      setWithheldCount(response.data?.withheldCount ?? 0);
      setOptions(
        (response.data?.companies ?? []).map((company) => ({
          name: company.name,
          value: company.id,
        }))
      );
    };

    fetchOptions().catch(() => {
      if (!cancelled) {
        setOptionsError('Could not load the list of companies.');
        setOptions([]);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [open, sourceCompanyId]);

  const jobNumbers = useMemo(
    () => (jobs ?? []).map((job) => `#${job.jobNumber}`).join(', '),
    [jobs]
  );

  // Escape and the close icon both land here; ignore them mid-request so the
  // modal cannot disappear while a transfer is still in flight.
  const requestClose = () => {
    if (isSubmitting) return;
    handleClose();
  };

  const onSubmit = async () => {
    if (!targetCompanyId || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const payload = {
        jobIds: jobs.map((job) => job.id),
        targetCompanyId: targetCompanyId,
      };
      const response = await ApiUtils.makeApiCall(
        'POST',
        'jobs/transfer-company',
        JSON.stringify(payload)
      );

      if (response?.status === 'success') {
        const {transferredCount, transferredFileCount, toCompany} = response.data ?? {};
        const attachments = transferredFileCount
          ? ` and ${transferredFileCount} attachment(s)`
          : '';
        toast.success(
          `${transferredCount} draft(s)${attachments} moved to ${toCompany?.name ?? 'the selected company'}.`,
          {toastId: `success_${TOAST_ID}`}
        );
        handleClose();
        onTransferred?.();
      } else {
        // The server rejects the whole batch and names every offending job, so its
        // message is more useful here than anything generic.
        toast.error(
          response?.message || 'Failed to transfer the selected drafts.',
          {toastId: `error_${TOAST_ID}`}
        );
      }
    } catch (e) {
      toast.error('An error occurred while transferring the selected drafts.', {
        toastId: `error_${TOAST_ID}`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  const isLoadingOptions = options === null;
  const hasOptions = (options ?? []).length > 0;

  // An empty list has two very different causes, and saying the wrong one sends
  // people looking for a company that is right there in the tree.
  const emptyListMessage = withheldCount > 0
    ? `This company tree has ${withheldCount} other active company(ies), but your account ` +
      'cannot transfer to them -- they sit outside the companies you administer. ' +
      'Ask an iPermit administrator to run the transfer.'
    : 'There is no other active company in this company tree to transfer to.';

  return (
    <ModalWrapper
      open={open}
      modalName={'transfer-jobs-company'}
      handleClose={requestClose}
      preventScroll={true}
      width={{xs: '98%', sm: '560px'}}
      padding={6}
      customStyle={{paddingTop: 2, minHeight: 'auto'}}
    >
      <ModalContentWrapper
        handleClose={requestClose}
        header={'Transfer Drafts'}
        note={{
          text: 'Only draft jobs can be transferred, and only to a company in the same company tree.',
          color: STYLE_CONSTANTS.COLORS.fontPale,
        }}
      >
        {isSubmitting && <ModalLoader/>}
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} method={'POST'}>
            <Grid container>
              <Grid item xs={12}>
                <Text text={`Moving ${jobs?.length ?? 0} draft(s): ${jobNumbers}`} size={'large'}/>
                <Spacer y={1}/>
                <Text
                  text={`From: ${sourceCompany?.name ?? ''}`}
                  size={'normal'}
                  pale={true}
                />
              </Grid>
              <Grid item xs={12}>
                <Spacer y={2}/>
                {isLoadingOptions && <Text text={'Loading companies...'} pale={true}/>}
                {!isLoadingOptions && optionsError && (
                  <Text text={optionsError} color={STYLE_CONSTANTS.COLORS.globalRed}/>
                )}
                {!isLoadingOptions && !optionsError && !hasOptions && (
                  <Text
                    text={emptyListMessage}
                    color={STYLE_CONSTANTS.COLORS.globalRed}
                  />
                )}
                {!isLoadingOptions && hasOptions && (
                  <CustomFormBlock
                    viewMode={false}
                    renderLabel={true}
                    isEditable={true}
                    field={TARGET_COMPANY_FIELD}
                    inputType={inputTypes.singleSelect}
                    options={options}
                    fullWidth
                  />
                )}
              </Grid>
              <Grid item xs={12}>
                <Box display={'flex'} justifyContent={'center'}>
                  <FormSubmitButtons
                    cancelAction={requestClose}
                    cancelText={'Cancel'}
                    submitText={'Transfer'}
                    disableSubmit={!targetCompanyId || isSubmitting || !hasOptions}
                    disableCancel={isSubmitting}
                  />
                </Box>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </ModalContentWrapper>
    </ModalWrapper>
  );
};

TransferJobsModal.propTypes = {
  open: PropTypes.bool,
  jobs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      jobNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  sourceCompany: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  handleClose: PropTypes.func.isRequired,
  onTransferred: PropTypes.func,
};

export default TransferJobsModal;
